import { redirect } from "@tanstack/react-router";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  AlertTriangle,
  ArrowLeft,
  Bookmark,
  CalendarDays,
  Clock,
  Loader2,
  MapPin,
  MessageCircle,
  RefreshCw,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { getJobPost, reportPost, toggleSavePost } from "@/lib/marketplace.functions";
import { unlockLead, LEAD_UNLOCK_COST } from "@/lib/leads.functions";
import { tradeForSlug, URGENCY_LABEL } from "@/lib/trades";
import { gbp, ukDateTime } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/job-post/$postId")({
  // Retired marketplace screen: hidden from visitors, data kept.
  beforeLoad: () => { throw redirect({ to: "/guides" }); },
  head: () => ({
    meta: [
      { title: "Job details — Shields" },
      { name: "description", content: "Full details of a job a customer needs doing." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: JobPostPage,
});

function JobPostPage() {
  const { postId } = Route.useParams();
  const fetchJob = useServerFn(getJobPost);
  const saveFn = useServerFn(toggleSavePost);
  const unlockFn = useServerFn(unlockLead);
  const reportFn = useServerFn(reportPost);
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);

  const query = useQuery({
    queryKey: ["job-post", postId],
    queryFn: () => fetchJob({ data: { postId } }),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["job-post", postId] });

  const save = useMutation({
    mutationFn: (v: { save: boolean }) => saveFn({ data: { postId, save: v.save } }),
    onSuccess: (r) => (r.ok ? (toast.success("Updated"), refresh()) : toast.error(r.error)),
  });

  const unlock = useMutation({
    mutationFn: () => unlockFn({ data: { customerId: query.data?.job?.authorId ?? "", message } }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error);
        return;
      }
      toast.success(`Message sent — ${LEAD_UNLOCK_COST} SC used`);
      setShowMessage(false);
      setMessage("");
      refresh();
    },
    onError: () => toast.error("Could not send that just now"),
  });

  const report = useMutation({
    mutationFn: () => reportFn({ data: { postId, reason: "Reported from job details" } }),
    onSuccess: (r) => (r.ok ? toast.success("Thanks — our team will review this") : toast.error(r.error)),
  });

  if (query.isPending) {
    return (
      <main className="section-pad mx-auto max-w-2xl py-10">
        <div className="h-64 w-full animate-pulse rounded-2xl bg-muted" />
        <div className="mt-4 h-5 w-2/3 animate-pulse rounded bg-muted" />
      </main>
    );
  }

  if (query.isError || !query.data?.job) {
    return (
      <main className="section-pad mx-auto max-w-2xl py-16 text-center">
        <p className="text-lg font-bold text-foreground">This job isn&apos;t available.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          It may have been claimed or taken down. Try the feed for jobs near you.
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <Button onClick={() => void query.refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden /> Retry
          </Button>
          <Button variant="outline" asChild>
            <Link to="/feed">Back to feed</Link>
          </Button>
        </div>
      </main>
    );
  }

  const { job, balance, alreadyUnlocked, canContact } = query.data;

  return (
    <main className="section-pad mx-auto max-w-2xl py-6 pb-28">
      <Link
        to="/feed"
        className="inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-foreground/70 hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Back to feed
      </Link>

      {job.imageUrl && (
        <img
          src={job.imageUrl}
          alt={job.title ?? "Job photo"}
          className="mt-4 w-full rounded-2xl border border-border object-cover"
        />
      )}

      <h1 className="mt-5 font-display text-2xl font-extrabold text-foreground">
        {job.title?.trim() || (job.body.split("\n")[0] ?? "Job wanted")}
      </h1>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm font-semibold">
        <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
          {tradeForSlug(job.serviceSlug) ?? job.serviceName ?? "General"}
        </span>
        <span
          className={`rounded-full px-3 py-1 ${
            job.urgency === "urgent"
              ? "bg-destructive/10 text-destructive"
              : "bg-muted text-foreground/70"
          }`}
        >
          {URGENCY_LABEL[job.urgency] ?? "Flexible"}
        </span>
        <span className="text-lg font-extrabold text-foreground">
          {job.budgetPence ? gbp(job.budgetPence) : "Open to quotes"}
        </span>
      </div>

      <ul className="mt-4 grid gap-2 rounded-2xl border border-border bg-card p-4 text-sm font-medium text-foreground">
        <li className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" aria-hidden />
          {job.areaLabel ?? "Approximate area shared with the customer's agreement"}
          {job.distanceMiles !== null && ` · ${job.distanceMiles.toFixed(1)} miles away`}
        </li>
        <li className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" aria-hidden /> Posted {ukDateTime(job.createdAt)}
        </li>
        {job.preferredDate && (
          <li className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" aria-hidden /> Preferred date {job.preferredDate}
          </li>
        )}
      </ul>

      <p className="mt-4 whitespace-pre-wrap text-base leading-relaxed text-foreground">{job.body}</p>

      <p className="mt-4 rounded-xl border border-dashed border-border p-3 text-xs font-medium text-foreground/70">
        The customer&apos;s exact address stays hidden until the job is booked and confirmed with you.
      </p>

      <div className="mt-6 grid gap-2">
        {canContact ? (
          alreadyUnlocked ? (
            <Button asChild>
              <Link to="/messages">
                <MessageCircle className="mr-2 h-4 w-4" aria-hidden /> Open your chats
              </Link>
            </Button>
          ) : showMessage ? (
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="text-sm font-bold text-foreground">Message this customer</p>
              <p className="mt-1 text-sm text-foreground/70">
                Cost: <strong>{LEAD_UNLOCK_COST} Shields Credits</strong> · Your balance:{" "}
                <strong>{balance} SC</strong>. Credits are taken when you send this message, once
                only — messaging the same customer again is free.
              </p>
              <Textarea
                className="mt-3"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Introduce yourself and how you'd tackle the job."
                aria-label="Your message to the customer"
              />
              <div className="mt-3 flex gap-2">
                <Button
                  disabled={message.trim().length < 2 || unlock.isPending || balance < LEAD_UNLOCK_COST}
                  onClick={() => unlock.mutate()}
                >
                  {unlock.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
                  Confirm and send ({LEAD_UNLOCK_COST} SC)
                </Button>
                <Button variant="outline" onClick={() => setShowMessage(false)}>
                  Cancel
                </Button>
              </div>
              {balance < LEAD_UNLOCK_COST && (
                <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-destructive">
                  <Wallet className="h-4 w-4" aria-hidden /> Not enough credits —{" "}
                  <Link to="/partner/wallet" className="underline">
                    top up
                  </Link>
                </p>
              )}
            </div>
          ) : (
            <Button onClick={() => setShowMessage(true)}>
              <MessageCircle className="mr-2 h-4 w-4" aria-hidden /> Message customer ({LEAD_UNLOCK_COST} SC)
            </Button>
          )
        ) : (
          <p className="rounded-xl border border-border bg-card p-3 text-sm font-medium text-foreground/70">
            Only approved Shield Partners can contact customers about a job.
          </p>
        )}

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => save.mutate({ save: !job.saved })}>
            <Bookmark className={`mr-2 h-4 w-4 ${job.saved ? "fill-primary text-primary" : ""}`} aria-hidden />
            {job.saved ? "Saved" : "Save job"}
          </Button>
          <Button variant="outline" className="flex-1" onClick={() => report.mutate()} disabled={report.isPending}>
            <AlertTriangle className="mr-2 h-4 w-4" aria-hidden /> Report
          </Button>
        </div>
      </div>
    </main>
  );
}
